import React, { useState, useMemo, useEffect } from "react";
import moment from "moment";
import dayjs from "dayjs";
import { DatePicker, Select } from "antd";
import { getUser } from "../../../utils/helpers";
import { migrate, migrateCheckDuplicate, migrateDelete } from "../../../actions/migrate";
import "./migrate.css";
import ResponseModal from "./ResponseModal";
import LoadingGif from "../../../assets/images/loading.gif";
import Error from "../../Error";

export default function Migrate() {
  const [date, setDate] = useState(moment(new Date()).format("YYYY-MM-DD"));
  const [showResponse, setShowResponse] = useState(false);
  const [message, setMessage] = useState("");
  const [missingTestErrors, setMissingTestErrors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("sample_preped");
  const [showError, setShowError] = useState({ show: false, message: "" });
  const user = useMemo(() => getUser(), []);
  const categoryOptions = useMemo(() => [
    { value: "all", label: "All", permission: "migrate_all" },
    { value: "sample_preped", label: "SamplePreped", permission: "no-permission" },
    { value: "not_sample_preped", label: "Not SamplePreped", permission: "migrate_not_sample_preped" },
    { value: "holden", label: "Holden", permission: "migrate_holden" },
  ], []);
  const [authorizedOptions, setAuthorizedOptions] = useState([{ value: "sample_preped", label: "SamplePreped" }]);

  useEffect(() => {
    const options = categoryOptions.reduce((acc, item) => {
      const { permission } = item;
      if (permission === "no-permission" || user.permissionMap[permission]) {
        acc.push(item);
      }
      return acc;
    }, []);
    setAuthorizedOptions(options);
  }, [user, categoryOptions]);
  const toggleError = (error) => {
    setShowError(error);
  };

  function handleDate(e) {
    const dateValue = e === null ? "Invalid date" : moment(e.$d).format("YYYY-MM-DD");
    setDate(dateValue);
  }

  const toggleModal = () => {
    if (showResponse) {
      setMissingTestErrors([]);
    }
    setShowResponse(!showResponse);
  };

  function loadingSet(value) {
    setLoading(value);
  }

  const checkMissingTestErrors = (apiResponse) => {
    if (apiResponse.missing_test_errors && selectedCategory === "all") { // Show the missing test strings only if the category is "all"
      setMissingTestErrors(apiResponse.missing_test_errors);
    }
  };

  const apiMigrate = async () => {
    loadingSet(true);
    if (date === "Invalid date") {
      const error = { show: true, message: "Select a Valid Date" };
      toggleError(error);
      loadingSet(false);
      return;
    }
    const payload = {
      date: moment(date).format("MM/DD/YYYY"),
      category: selectedCategory,
    };
    const response = await migrate(payload);
    if (response.success) {
      setMessage(response.message);
      checkMissingTestErrors(response);
      toggleModal();
      loadingSet(false);
    } else {
      const error = { show: true, message: response.message };
      loadingSet(false);
      toggleError(error);
    }
  };

  const apiCheckDuplicate = async () => {
    loadingSet(true);
    if (date === "Invalid date") {
      const error = { show: true, message: "Select a Valid Date" };
      toggleError(error);
      loadingSet(false);
      return;
    }
    const payload = moment(date).format("MM/DD/YYYY");
    const response = await migrateCheckDuplicate(payload);
    if (response.success) {
      setMessage(response.message);
      toggleModal();
      loadingSet(false);
    } else {
      const error = { show: true, message: response.message };
      loadingSet(false);
      toggleError(error);
    }
  };

  const apiDeleteMigrate = async () => {
    loadingSet(true);
    if (date === "Invalid date") {
      const error = { show: true, message: "Select a Valid Date" };
      toggleError(error);
      loadingSet(false);
      return;
    }
    const payload = moment(date).format("MM/DD/YYYY");
    const response = await migrateDelete(payload);
    if (response.success) {
      setMessage(response.message);
      loadingSet(false);
    } else {
      const error = { show: true, message: response.message };
      toggleModal();
      toggleError(error);
      loadingSet(false);
    }
  };

  return (
    <div className="customMigrate">
      <div className="migrateInputs">
        <DatePicker
          showNow
          allowClear={false}
          format="MM/DD/YYYY"
          value={(date === "" || date === "Invalid date") ? "" : dayjs(date)}
          defaultValue={(date === "" || date === "Invalid date") ? "" : dayjs(date)}
          className="migrateDate"
          onChange={(e) => handleDate(e)}
          placeholder="mm/dd/yyyy"
        />
        <Select
          className="MigrateCategorySelect"
          options={authorizedOptions}
          defaultValue={selectedCategory}
          onChange={setSelectedCategory}
        />
        <button type="button" className="migrateBtn" onClick={apiMigrate}>
          Migrate
        </button>
        <button type="button" className="migrateBtn" onClick={apiCheckDuplicate}>
          Check Duplicates
        </button>
      </div>
      {showResponse && (
        <ResponseModal
          message={message}
          toggleModal={toggleModal}
          handleDelete={apiDeleteMigrate}
          missingTestErrors={missingTestErrors}
        />
      )}
      {loading && (
        <div className="loadingIndicator">
          <img src={LoadingGif} alt="Loading gif" />
        </div>
      )}
      {showError.show && (
        <Error message={showError.message} toggleError={toggleError} />
      )}
    </div>
  );
}
